<script setup lang="ts">
import { Hourglass } from 'lucide-vue-next'
import type { RadioCardOption } from '@/components/ui/AppRadioCard.vue'

type ExpirationType = 'point_relative' | 'after_registration' | 'absolute_date'
type Scope = 'new' | 'all'

const props = defineProps<{
  isOpen: boolean
  isProcessing?: boolean
  type: ExpirationType | null
  unit: 'day' | 'month' | 'year' | null
  value: number | string | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'confirm', applyToExistingPoints: boolean): void
}>()

const { t } = useI18n()

const selectedScope = ref<RadioCardOption | null>(null)

const durationLabel = computed(() => {
  if (props.unit == null) return ''
  const count = Number(props.value)
  if (props.unit === 'year') return t('pages.loyaltyPage.tabs.pointExpiration.confirm.durationYears', { count })
  if (props.unit === 'month') return t('pages.loyaltyPage.tabs.pointExpiration.confirm.durationMonths', { count })
  return t('pages.loyaltyPage.tabs.pointExpiration.confirm.durationDays', { count })
})

const dateLabel = computed(() => {
  const [month, day] = String(props.value ?? '').split('-')
  return month && day ? `${Number(day)} ${t(`global.months.${month}`)}` : ''
})

const modeKey = computed(() => {
  if (props.type === 'absolute_date') return 'absolute'
  if (props.type === 'after_registration') return 'afterRegistration'
  return 'relative'
})

const translateForMode = (group: 'rule' | 'existingPoints') =>
  t(`pages.loyaltyPage.tabs.pointExpiration.confirm.${group}.${modeKey.value}`, {
    duration: durationLabel.value,
    date: dateLabel.value
  })

const ruleText = computed(() => (props.type == null ? '' : translateForMode('rule')))

const choices = computed<RadioCardOption[]>(() => [
  {
    id: 'new',
    value: 'new' satisfies Scope,
    title: t('pages.loyaltyPage.tabs.pointExpiration.confirm.option.new.title'),
    description: t('pages.loyaltyPage.tabs.pointExpiration.confirm.option.new.description'),
    disabled: props.isProcessing
  },
  {
    id: 'all',
    value: 'all' satisfies Scope,
    title: t('pages.loyaltyPage.tabs.pointExpiration.confirm.option.all.title'),
    description: props.type == null ? '' : translateForMode('existingPoints'),
    disabled: props.isProcessing
  }
])

const handleConfirm = () => {
  if (props.isProcessing || selectedScope.value == null) return
  emit('confirm', selectedScope.value.value === 'all')
}

const handleClose = () => {
  if (props.isProcessing) return
  emit('close')
}

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) selectedScope.value = null
  }
)
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    size="lg"
    :link-text="t('pages.loyaltyPage.tabs.pointExpiration.confirm.confirmButton')"
    :cancel="t('ui.confirmModal.buttons.cancel')"
    :is-processing="isProcessing"
    :is-disabled="selectedScope == null"
    :is-closable="!isProcessing"
    @close="handleClose"
    @click="handleConfirm"
  >
    <template #customHeadingTitle>
      <h3 class="font-semibold text-foreground">
        {{ t('pages.loyaltyPage.tabs.pointExpiration.confirm.title') }}
      </h3>
    </template>

    <div class="flex flex-col gap-5">
      <div class="flex items-center gap-3 rounded-lg border border-border bg-muted p-4 text-sm">
        <div class="shrink-0 rounded-xl border border-border bg-white p-2">
          <Hourglass class="h-5 w-5 text-foreground-secondary" />
        </div>
        <span class="font-medium text-foreground" data-test="expiration-rule">{{ ruleText }}</span>
      </div>

      <p class="font-medium text-foreground">
        {{ t('pages.loyaltyPage.tabs.pointExpiration.confirm.question') }}
      </p>

      <AppRadioCard v-model="selectedScope" class="grid grid-cols-1 gap-2.5" :options="choices" />

      <BaseTip
        v-if="selectedScope?.value === 'all'"
        :text="t('pages.loyaltyPage.tabs.pointExpiration.confirm.irreversible')"
        severity="warn"
      />
    </div>
  </BaseModal>
</template>
